import React, { useEffect, useState } from "react";
import { Wallet, ChevronRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { fmtMoney, fmtDateShort } from "@/lib/format";
import { PageHeader, Loading } from "@/components/common";

const NEXT = { "Completed": "Ready for Invoicing", "Ready for Invoicing": "Invoiced", "Invoiced": "Closed" };
const STAGE_COLOR = { "Completed": "border-t-slate-400", "Ready for Invoicing": "border-t-violet-500", "Invoiced": "border-t-blue-500", "Closed": "border-t-emerald-500" };

export default function Invoicing() {
  const { user } = useAuth();
  const canApprove = can(user, "approve_invoicing");
  const [data, setData] = useState(null);

  const load = () => api.get("/invoicing/board").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;

  const advance = async (v, to) => {
    let invoice_number = null;
    if (to === "Invoiced") { invoice_number = window.prompt("Invoice number (optional):") || null; }
    try { await api.post(`/visits/${v.id}/invoice-advance`, { to_status: to, invoice_number }); toast.success(`Moved to ${to}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Invoicing" subtitle="Ready-for-Invoicing workflow — from completed work to invoiced & paid" icon={Wallet} />
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {data.stages.map((stage) => (
          <div key={stage} className={`bg-slate-50 border border-slate-200 border-t-4 ${STAGE_COLOR[stage]} rounded-xl flex flex-col`}>
            <div className="px-3 py-2.5 border-b border-slate-200">
              <div className="text-sm font-display font-semibold text-slate-800">{data.labels[stage]}</div>
              <div className="flex items-center justify-between mt-0.5">
                <span className="text-xs text-slate-400">{data.board[stage].length} visits</span>
                <span className="text-sm font-semibold text-seq-green">{fmtMoney(data.totals[stage])}</span>
              </div>
            </div>
            <div className="p-2 space-y-2 min-h-[200px] max-h-[65vh] overflow-y-auto" data-testid={`invoice-col-${stage.replace(/\s+/g, "-")}`}>
              {data.board[stage].length === 0 && <div className="text-xs text-slate-300 text-center py-8">Empty</div>}
              {data.board[stage].map((v) => (
                <div key={v.id} className="bg-white border border-slate-200 rounded-lg p-2.5" data-testid={`invoice-card-${v.id}`}>
                  <div className="text-sm font-medium text-slate-700 truncate">{v.job_title}</div>
                  <div className="text-xs text-slate-400 truncate">{v.client_name}</div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-slate-400">{fmtDateShort(v.scheduled_date)}</span>
                    <span className="text-sm font-semibold text-slate-700">{fmtMoney(v.charge)}</span>
                  </div>
                  {v.invoice_number && <div className="text-[10px] text-blue-500 mt-1">{v.invoice_number}</div>}
                  {canApprove && NEXT[stage] && (
                    <button onClick={() => advance(v, NEXT[stage])} data-testid={`advance-${v.id}`}
                      className="mt-2 w-full text-xs flex items-center justify-center gap-1 py-1 rounded bg-seq-green/10 text-seq-green font-medium hover:bg-seq-green/20">
                      Move to {NEXT[stage]} <ChevronRight size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
